import { useState, useRef, useCallback } from 'react';
import {
  Upload, Scissors, Wrench, Sparkle, TreePine,
  Building2, Dumbbell, Car, Package, CheckCircle2,
} from 'lucide-react';
import { palette, admin, transition, fontFamily, settingsHub } from '../../../styles/tokens';
import { SettingsSubPage } from '../components/SettingsSubPage';
import { Toast } from '../../../components/common/Toast';
import { useConfigImport } from '../hooks/useConfigImport';
import { STARTER_PACKS } from '../../../data/starterPacks';
import type { DiffSection } from '../hooks/useConfigImport';

const PACK_ICONS: Record<string, { icon: typeof Scissors; bg: string; fg: string }> = {
  nail_salon: { icon: Scissors, bg: settingsHub.iconOrangeBg, fg: settingsHub.iconOrangeFg },
  barbershop: { icon: Scissors, bg: settingsHub.iconPurpleBg, fg: settingsHub.iconPurpleFg },
  plumbing: { icon: Wrench, bg: settingsHub.iconBlueBg, fg: settingsHub.iconBlueFg },
  cleaning: { icon: Sparkle, bg: settingsHub.iconGreenBg, fg: settingsHub.iconGreenFg },
  landscaping: { icon: TreePine, bg: settingsHub.iconLimeBg, fg: settingsHub.iconLimeFg },
  property: { icon: Building2, bg: settingsHub.iconAmberBg, fg: settingsHub.iconAmberFg },
  fitness: { icon: Dumbbell, bg: settingsHub.iconPurpleBg, fg: settingsHub.iconPurpleFg },
  automotive: { icon: Car, bg: settingsHub.iconOrangeBg, fg: settingsHub.iconOrangeFg },
};

export function ImportConfigPage() {
  const {
    parseFile, parseStarterPack, parsed, validationErrors,
    diff, applyConfig, applying, applied, applyError, applySummary, reset,
  } = useConfigImport();
  const [toast, setToast] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  }, [parseFile]);

  const handleFileSelect = useCallback(() => {
    const file = fileRef.current?.files?.[0];
    if (file) parseFile(file);
    if (fileRef.current) fileRef.current.value = '';
  }, [parseFile]);

  // State 3: Applied
  if (applied) {
    return (
      <SettingsSubPage title="Import config" description="Configuration applied">
        <div style={{
          background: palette.white,
          borderRadius: admin.cardRadius,
          border: `1px solid ${admin.border}`,
          padding: '32px 24px',
          textAlign: 'center',
        }}>
          <CheckCircle2 size={36} color={palette.green} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 600, color: palette.g800, marginBottom: 8 }}>
            Configuration applied!
          </div>
          <div style={{ fontSize: 13, color: palette.g500, lineHeight: 1.6, marginBottom: 4 }}>
            {applySummary.join(' \u00b7 ')}
          </div>
          {applyError && (
            <div style={{
              marginTop: 12, padding: '10px 16px', borderRadius: 8,
              background: palette.amberWash, border: `1px solid ${palette.amber}`,
              fontSize: 12.5, color: '#92400E', textAlign: 'left',
            }}>
              {applyError}
            </div>
          )}
          <button
            onClick={reset}
            style={{
              marginTop: 20, padding: '10px 20px', borderRadius: 8,
              border: `1px solid ${admin.border}`, background: palette.white,
              fontSize: 13, fontWeight: 500, color: palette.g600,
              cursor: 'pointer', fontFamily,
            }}
          >
            Import another
          </button>
        </div>
      </SettingsSubPage>
    );
  }

  // State 2: Preview (parsed config with diff)
  if (parsed && diff) {
    return (
      <SettingsSubPage title="Import config" description="Review changes before applying">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Config metadata */}
          <div style={{
            background: palette.white,
            borderRadius: admin.cardRadius,
            border: `1px solid ${admin.border}`,
            padding: '16px 20px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: parsed._isStarterPack ? settingsHub.iconAmberBg : palette.accentWash,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Package size={18} color={parsed._isStarterPack ? settingsHub.iconAmberFg : palette.accent} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>
                {parsed._exportedFrom}
                {parsed._isStarterPack && (
                  <span style={{
                    marginLeft: 8, fontSize: 10, fontWeight: 600,
                    padding: '2px 8px', borderRadius: 8,
                    background: settingsHub.iconAmberBg, color: settingsHub.iconAmberFg,
                  }}>
                    Starter Pack
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: palette.g400, marginTop: 2 }}>
                {parsed._industry} &middot; exported {parsed._exportedAt.slice(0, 10)}
              </div>
            </div>
          </div>

          {/* Validation errors */}
          {validationErrors.length > 0 && (
            <div style={{
              padding: '12px 16px', borderRadius: 8,
              background: palette.redWash, border: `1px solid ${palette.red}`,
              fontSize: 13, color: palette.red,
            }}>
              {validationErrors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}

          {/* Diff list */}
          <div style={{
            background: palette.white,
            borderRadius: admin.cardRadius,
            border: `1px solid ${admin.border}`,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '14px 20px',
              borderBottom: `1px solid ${palette.g100}`,
              fontSize: 14,
              fontWeight: 600,
              color: palette.g800,
            }}>
              Changes to apply
            </div>
            {diff.sections.map((section, i) => (
              <DiffRow key={section.key} section={section} isLast={i === diff.sections.length - 1} />
            ))}
            {diff.sections.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', fontSize: 13, color: palette.g400 }}>
                No changes detected in this config.
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={applyConfig}
              disabled={applying || diff.sections.filter(s => s.status === 'will_set' || s.status === 'will_change').length === 0}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 20px', borderRadius: 8,
                border: 'none',
                background: applying ? palette.g300 : palette.accent,
                fontSize: 13, fontWeight: 600, color: palette.white,
                cursor: applying ? 'default' : 'pointer', fontFamily,
                opacity: applying ? 0.7 : 1,
              }}
            >
              {applying ? 'Applying...' : 'Apply configuration'}
            </button>
            <button
              onClick={reset}
              style={{
                padding: '10px 20px', borderRadius: 8,
                border: `1px solid ${admin.border}`, background: palette.white,
                fontSize: 13, fontWeight: 500, color: palette.g600,
                cursor: 'pointer', fontFamily,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </SettingsSubPage>
    );
  }

  // State 1: Upload (default)
  return (
    <SettingsSubPage title="Import config" description="Upload a config file or start from an industry template">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? palette.accent : palette.g300}`,
            borderRadius: admin.cardRadius,
            padding: '48px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver ? palette.accentWash : palette.white,
            transition: `all ${transition.fast}`,
          }}
        >
          <Upload size={28} color={dragOver ? palette.accent : palette.g400} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 14, fontWeight: 500, color: palette.g700, marginBottom: 4 }}>
            Drop a .json config file here or click to browse
          </div>
          <div style={{ fontSize: 12, color: palette.g400 }}>
            Accepts crewbit-config-*.json files
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {/* Validation errors (from file parse attempt) */}
        {validationErrors.length > 0 && (
          <div style={{
            padding: '12px 16px', borderRadius: 8,
            background: palette.redWash, border: `1px solid ${palette.red}`,
            fontSize: 13, color: palette.red,
          }}>
            {validationErrors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}

        {/* Starter packs */}
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: palette.g800, marginBottom: 4 }}>
            Or start from a template
          </div>
          <div style={{ fontSize: 13, color: palette.g500, marginBottom: 16 }}>
            Pre-built industry starter packs with services, hours, and defaults
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 10,
          }}>
            {STARTER_PACKS.map(pack => {
              const visual = PACK_ICONS[pack.id] ?? { icon: Package, bg: palette.g100, fg: palette.g500 };
              const Icon = visual.icon;
              return (
                <StarterPackCard
                  key={pack.id}
                  label={pack.name}
                  serviceCount={pack.serviceCount}
                  icon={<Icon size={18} color={visual.fg} strokeWidth={1.8} />}
                  bg={visual.bg}
                  onClick={() => parseStarterPack(pack.id)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {toast && (
        <Toast message={toast} type="success" isVisible={!!toast} onDismiss={() => setToast(null)} />
      )}
    </SettingsSubPage>
  );
}

// ── Diff row ──

const STATUS_BADGE: Record<DiffSection['status'], { label: string; bg: string; fg: string } | null> = {
  will_set: { label: 'New', bg: palette.greenWash, fg: palette.green },
  will_change: { label: 'Update', bg: palette.amberWash, fg: palette.amber },
  already_matches: { label: 'Already set', bg: palette.g100, fg: palette.g500 },
  skipped: null,
};

function DiffRow({ section, isLast }: { section: DiffSection; isLast: boolean }) {
  const badge = STATUS_BADGE[section.status];
  if (!badge) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 20px',
      borderBottom: isLast ? 'none' : `1px solid ${palette.g50}`,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: palette.g700 }}>
          {section.label}
          {section.itemCount != null && section.itemCount > 0 && (
            <span style={{ fontSize: 12, color: palette.g400, marginLeft: 6 }}>
              ({section.itemCount})
            </span>
          )}
        </div>
        {section.details && (
          <div style={{ fontSize: 12, color: palette.g400, marginTop: 2 }}>{section.details}</div>
        )}
      </div>
      <span style={{
        fontSize: 10.5, fontWeight: 600,
        padding: '3px 8px', borderRadius: 8,
        background: badge.bg, color: badge.fg,
        whiteSpace: 'nowrap',
      }}>
        {badge.label}
      </span>
    </div>
  );
}

// ── Starter pack card ──

function StarterPackCard({
  label, serviceCount, icon, bg, onClick,
}: {
  label: string;
  serviceCount: number;
  icon: React.ReactNode;
  bg: string;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 14px',
        borderRadius: 10,
        border: `0.5px solid ${hovered ? admin.borderHover : palette.g200}`,
        background: palette.white,
        cursor: 'pointer',
        fontFamily,
        textAlign: 'left',
        transition: `border-color ${transition.fast}`,
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: palette.g700 }}>{label}</div>
        <div style={{ fontSize: 11, color: palette.g400, marginTop: 1 }}>
          {serviceCount} services
        </div>
      </div>
      <Package size={12} color={palette.g400} style={{ flexShrink: 0 }} />
    </button>
  );
}
